import React, { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { MDialog } from 'common';

import { useUserRolePermissions } from 'hooks';
import * as R from 'ramda';
import { format, isBefore, isValid, parseISO, startOfToday } from 'date-fns';
import { GT_ZERO, REQUIRED } from 'utils/helpers/validationMessages';
import { func, number, PropTypes, string } from 'prop-types';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { capitalizeSingleWord, formatNumberWithCommas } from 'utils/helpers/format';

import palette from 'library/theme/palette';
import renderInput from './renderUtils';

import {
  useCreateReconsignmentMutation,
  useEditReconsignmentMutation,
  useGetAffiliateProductsQuery,
  useGetDestinationsQuery,
  useGetFromLocationListQuery,
  useGetReconsignmentQuery,
  useGetReidVaporPressureQuery,
  useGetViewReconsignmentQuery,
} from '../reconsignmentsEndpoints';
import { INVENTORY_STATUS_OPTIONS } from '../utils/constant';

// Update the formatWithTimezone function
const formatWithTimezone = date => {
  if (!date) return '';
  // Set time to middle of day (noon) UTC to avoid any timezone crossing issues
  const utcDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0);
  return format(utcDate, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone: 'UTC' });
};

const ManualDrawer = ({
  reconsignmentInventoryOwner,
  selectedReconsignment,
  setSelectedReconsignment,
  modalMode,
  onReconsignmentUpdate,
}) => {
  const { currentAffiliate } = useUserRolePermissions();

  const [reconsignment, setReconsignment] = useState({});
  const [validation, setValidation] = useState({
    rvp: '',
    toLocation: false,
    volume: '',
  });
  const [disableSave, setDisableSave] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);

  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const [initialReconsignment, setInitialReconsignment] = useState({});
  const isDateInvalidOrPast = date => !isValid(date) || date < startOfToday();

  const [validationState, setValidationState] = useState({
    transferDate: '',
  });

  const params = {
    inventoryOwner: {
      companyCustomerIdName: reconsignmentInventoryOwner
        ? reconsignmentInventoryOwner.customerIdName
        : currentAffiliate?.customerIdName,
      dataCompanyId: reconsignmentInventoryOwner
        ? reconsignmentInventoryOwner.dataCompanyId
        : currentAffiliate?.dataCompanyId,
    },
    reconsignmentId: selectedReconsignment,
  };

  const { currentData: editData } = useGetReconsignmentQuery(
    { params },
    { skip: R.isNil(selectedReconsignment) || modalMode === 'view' }
  );

  const { currentData: viewData, error: viewError } = useGetViewReconsignmentQuery(
    { params },
    { skip: R.isNil(selectedReconsignment) || modalMode === 'edit' }
  );

  const currentData = useMemo(
    () => (modalMode === 'view' ? { reconsignment: viewData } : editData),
    [modalMode, viewData, editData]
  );

  const { currentData: products = [], isFetching: isLoadingProducts } =
    useGetAffiliateProductsQuery(
      { params },
      { skip: R.isNil(currentAffiliate) || modalMode === 'view' }
    );

  const isGasoline = R.path(['product', 'isGasoline'], reconsignment) === true;

  const destinationParams = R.assoc(
    'productId',
    R.path(['product', 'productId'], reconsignment),
    params
  );
  const locationIdParams = R.assoc(
    'locationId',
    R.path(['fromLocation', 'locationId'], reconsignment),
    destinationParams
  );

  const { currentData: rvpData = [] } = useGetReidVaporPressureQuery(
    { params: destinationParams },
    { skip: R.not(isGasoline) || modalMode === 'view' }
  );

  const rvps = R.prepend('Any', rvpData);

  const destinationSkip = [
    R.isNil(currentAffiliate),
    R.isEmpty(reconsignment),
    R.isNil(R.path(['product', 'productId'], reconsignment)),
    R.isNil(R.path(['fromLocation', 'locationId'], reconsignment)),
  ];

  const { currentData: destinations = [], isFetching: isLoadingDestinations } =
    useGetDestinationsQuery(
      { params: locationIdParams },
      { skip: R.any(R.equals(true))(destinationSkip) || modalMode === 'view' }
    );

  const hasFromLocation = R.not(R.isNil(R.path(['fromLocation', 'locationId'], reconsignment)));
  const selectedToLocationId = R.path(['toLocation', 'locationId'], reconsignment);

  const locationParams = {
    ...params,
    productId: R.path(['product', 'productId'], reconsignment),
    rvp: isGasoline ? R.prop('rvpValue', reconsignment) : null,
  };

  const locationSkip = [
    R.isNil(currentAffiliate),
    R.isEmpty(reconsignment),
    R.isNil(R.path(['product', 'productId'], reconsignment)),
  ];

  const { currentData: locations = [], isFetching: isLoadingLocations } =
    useGetFromLocationListQuery(
      { params: locationParams },
      {
        skip: R.any(R.equals(true))(locationSkip) || modalMode === 'view',
      }
    );
  const stripTimeFromDate = date => {
    if (!R.isNil(date)) {
      return parseISO(date.split('T')[0]);
    }
    return parseISO(date);
  };

  useEffect(() => {
    const loaded = R.prop('reconsignment', currentData);
    if (!loaded) return;
    const loadedIsGasoline = R.path(['product', 'isGasoline'], loaded) === true;
    const parsed = isValid(parseISO(loaded.transferDate))
      ? stripTimeFromDate(loaded.transferDate)
      : null;
    const isPast = modalMode === 'view' ? false : !parsed || parsed < startOfToday();
    const normalizedBase = {
      ...loaded,
      rvpValue: loadedIsGasoline ? loaded.rvpValue ?? 'Any' : '',
    };
    const normalized = {
      ...normalizedBase,
      transferDate: format(isPast ? startOfToday() : parsed, "yyyy-MM-dd'T'HH:mm:ssXXX"),
    };
    setReconsignment(normalized);
    const initialForComparison = isPast
      ? { ...normalized, transferDate: loaded.transferDate }
      : normalized;
    setInitialReconsignment(initialForComparison);
  }, [currentData, modalMode]);

  useEffect(() => {
    if (modalMode === 'view' || isLoadingDestinations || R.isNil(selectedToLocationId)) {
      return;
    }

    const hasMatchingDestination = R.any(
      R.propEq('locationId', selectedToLocationId),
      destinations
    );

    if (!hasMatchingDestination) {
      setReconsignment(prev => R.assoc('toLocation', null, prev));
      setValidation(prev => R.assoc('toLocation', true, prev));
    }
  }, [destinations, isLoadingDestinations, modalMode, selectedToLocationId]);

  const todayOrTransferDate = R.ifElse(
    R.isNil,
    R.always(startOfToday()),
    R.pipe(stripTimeFromDate, date => (R.gte(date, startOfToday()) ? date : startOfToday()))
  );

  const transferDateValueForModalMode = transferDate =>
    R.equals('copy', modalMode)
      ? todayOrTransferDate(transferDate)
      : R.equals('edit', modalMode)
      ? stripTimeFromDate(transferDate)
      : stripTimeFromDate(transferDate);

  const transferDateValue = R.ifElse(
    R.pipe(R.prop('transferDate'), R.complement(R.isNil)),
    R.pipe(R.prop('transferDate'), transferDateValueForModalMode),
    R.always(null)
  )(reconsignment);

  const body = R.not(R.isEmpty(reconsignment)) && {
    fromLocationId: R.path(['fromLocation', 'locationId'], reconsignment),
    inventoryOwner: {
      companyCustomerIdName: reconsignmentInventoryOwner
        ? reconsignmentInventoryOwner.customerIdName
        : R.prop('customerIdName', currentAffiliate),
      dataCompanyId: reconsignmentInventoryOwner
        ? reconsignmentInventoryOwner.dataCompanyId
        : R.prop('dataCompanyId', currentAffiliate),
    },
    productId: R.path(['product', 'productId'], reconsignment),
    reconsignmentId: R.prop('reconsignmentId', reconsignment),
    rvp: isGasoline
      ? R.ifElse(
          R.or(R.equals('Any'), R.equals('')),
          R.always(null),
          R.always(R.prop('rvpValue', reconsignment))
        )(R.prop('rvpValue', reconsignment))
      : null,
    toLocationId: R.path(['toLocation', 'locationId'], reconsignment),
    transferDate: R.isNil(R.prop('transferDate', reconsignment))
      ? ''
      : formatWithTimezone(transferDateValue),
    volume: R.prop('volume', reconsignment),
  };
  const [submit, { isLoading: isUpdating }] = useEditReconsignmentMutation();
  const [submitCreate] = useCreateReconsignmentMutation();

  const dispatch = useDispatch();

  useEffect(() => {
    if (viewError) {
      dispatch(
        createSnack({
          message: viewError.data.title,
          options: { variant: 'error' },
        })
      );
      setReconsignment({});
      setSelectedReconsignment(null);
    }
  }, [viewError, setSelectedReconsignment, dispatch]);

  const handleSave = () => {
    submit({ body })
      .then(res => {
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: res.data.message,
            })
          );
          if (onReconsignmentUpdate) {
            onReconsignmentUpdate(reconsignment);
          }
        }
      })
      .finally(() => setTimeout(() => setSelectedReconsignment(null), 500));
  };

  const handleSubmit = () => {
    const cloneBody = {
      ...(R.omit(['fromLocationId', 'toLocationId', 'transferDate'], body) || {}),
      consignmentStatus: R.prop('consignmentStatus', reconsignment),
      fromLocation: R.path(['fromLocation', 'locationId'], reconsignment),
      inventoryOwner: {
        companyCustomerIdName: R.prop('customerIdName', currentAffiliate),
        dataCompanyId: R.prop('dataCompanyId', currentAffiliate),
      },
      inventoryStatus: R.prop('inventoryStatus', reconsignment),
      originIds: R.pluck('originId', R.prop('origins', reconsignment)),
      productId: R.path(['product', 'productId'], reconsignment),
      productStatus: R.path(['product', 'productStatus'], reconsignment),
      reconsignmentId: R.prop('reconsignmentId', reconsignment),
      rvp: isGasoline
        ? R.ifElse(
            R.or(R.equals('Any'), R.equals('')),
            R.always(null),
            R.always(R.prop('rvpValue', reconsignment))
          )(R.prop('rvpValue', reconsignment))
        : null,
      toLocation: R.path(['toLocation', 'locationId'], reconsignment),
      transferDate: R.isNil(R.prop('transferDate', reconsignment))
        ? ''
        : R.prop('transferDate', reconsignment),
      volume: R.prop('volume', reconsignment),
    };

    submitCreate({ body: cloneBody })
      .then(res => {
        if (res.error) {
          dispatch(
            createSnack({
              message: res.error.data.title,
              options: { variant: 'error' },
            })
          );
        } else {
          dispatch(
            createSnack({
              message: `Reconsignment #${res.data.reconsignmentId} has been successfully created.`,
            })
          );
          setSelectedReconsignment(null);
        }
      })
      .finally(() => setTimeout(() => setSelectedReconsignment(null), 500));
  };

  // validateVolume :: Number -> String
  const validateVolume = R.cond([
    [R.equals(NaN), R.always(REQUIRED)],
    [R.gte(0), R.always(GT_ZERO)],
    [R.T, R.always('')],
  ]);

  const sanitizeVolume = R.compose(R.replace(/[^0-9]/g, ''), R.path(['target', 'value']));

  const handleVolumeChange = R.compose(
    setReconsignment,
    R.assoc('volume'),
    R.tap(R.compose(setValidation, R.assoc('volume'), validateVolume)),
    parseInt,
    sanitizeVolume
  );

  const validateTransferDate = date => {
    if (R.isNil(date)) {
      setValidationState(R.assoc('transferDate', 'Date required'));
    } else if (date.toString() === 'Invalid Date') {
      setValidationState(R.assoc('transferDate', 'Invalid Date'));
    } else if (isBefore(new Date(date), startOfToday())) {
      setValidationState(R.assoc('transferDate', 'Transfer Date must be today or later'));
    } else {
      setValidationState(R.assoc('transferDate', ''));
    }
  };

  const handleDateChange = date => {
    validateTransferDate(date);
    if (isValid(date)) {
      R.compose(setReconsignment, R.assoc('transferDate'), R.always(format(date, 'yyyy-MM-dd')))();
    }
  };

  const helperTextCondition = validationState.transferDate;

  // Validations
  const isToLocationValid = R.not(R.isNil(R.prop('toLocation', reconsignment)));
  const isFromLocationValid = R.not(R.isNil(R.prop('fromLocation', reconsignment)));
  // rvp must not be empty when product isGasoline
  // not empty or not null
  const isRvpValid = isGasoline
    ? R.and(
        R.not(R.isEmpty(R.prop('rvpValue', reconsignment))),
        R.not(R.isNil(R.prop('rvpValue', reconsignment)))
      )
    : true;
  // volume must be greater than 0 & whole int
  const isVolumeValid = R.and(
    R.gte(R.prop('volume', reconsignment), 1),
    R.not(R.isEmpty(R.prop('volume', reconsignment)))
  );
  const isTransferDateValid = R.isEmpty(validationState.transferDate);

  const isRvpValueInApi = rvps.some(rvp => rvp === R.prop('rvpValue', reconsignment));

  const hasUpdates = !R.equals(reconsignment, initialReconsignment);

  const hasLocations = R.pipe(R.length, R.gt(R.__, 0))(locations);

  const validations = [
    isToLocationValid,
    isFromLocationValid,
    hasLocations,
    isRvpValid,
    isVolumeValid,
    isTransferDateValid,
  ];
  const hasValidInputs = R.all(R.equals(R.T()))(validations);

  useEffect(() => {
    if (modalMode === 'copy') {
      setDisableSave(!hasValidInputs);
    } else {
      setDisableSave(!(initialReconsignment && hasUpdates && hasValidInputs));
    }
  }, [modalMode, initialReconsignment, hasUpdates, hasValidInputs]);

  const handleClose = () => {
    if (hasUpdates) {
      setOpenDialog(true);
    } else {
      setReconsignment({});
      setSelectedReconsignment(null);
    }
  };

  const rvpChecks = [
    R.isEmpty(R.prop('rvpValue', reconsignment)),
    R.isNil(R.prop('rvpValue', reconsignment)),
    R.not(isRvpValueInApi) && modalMode !== 'view',
  ];

  const rvpValidation = R.and(isGasoline, R.any(R.equals(true))(rvpChecks));

  const isError = !R.isEmpty(validationState.transferDate);

  const renderStatus = R.cond([
    [
      R.equals('BOOKED'),
      R.always(
        <Chip
          color="success"
          label="Booked"
          sx={{
            '& > .MuiChip-label': { px: 0 },
            borderRadius: 0.5,
            fontSize: 12,
            height: 20,
            px: 1,
            py: 0.5,
            width: 65,
          }}
        />
      ),
    ],
    [
      R.equals('CANNOT BOOK'),
      R.always(
        <Chip
          color="error"
          label="Cannot Book"
          sx={{
            '& > .MuiChip-label': { px: 0 },
            borderRadius: 0.5,
            fontSize: 12,
            height: 20,
            px: 1,
            py: 0.5,
            width: 92,
          }}
        />
      ),
    ],
    [
      R.equals('SCHEDULED'),
      R.always(
        <Chip
          label="Scheduled"
          sx={{
            '& > .MuiChip-label': { px: 0 },
            backgroundColor: palette.success.light,
            borderRadius: 0.5,
            fontSize: 12,
            height: 20,
            maxWidth: 'none',
            px: 1,
            py: 0.5,
            width: 'min-content',
          }}
        />
      ),
    ],
    [
      R.equals('EXPIRED'),
      R.always(
        <Chip
          color="info"
          label="Expired"
          sx={{
            '& > .MuiChip-label': { px: 0 },
            borderRadius: 0.5,
            fontSize: 12,
            height: 20,
            px: 1,
            py: 0.5,
            width: 65,
          }}
        />
      ),
    ],
    [
      R.equals('CANCELED'),
      R.always(
        <Chip
          color="info"
          label="Canceled"
          sx={{
            '& > .MuiChip-label': { px: 0 },
            borderRadius: 0.5,
            fontSize: 12,
            height: 20,
            px: 1,
            py: 0.5,
            width: 75,
          }}
        />
      ),
    ],
  ]);

  return (
    <>
      <Drawer
        anchor="right"
        onClose={() => handleClose()}
        open
        slotProps={{
          paper: {
            sx: {
              overflowY: 'hidden',
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              width: { xs: 1, lg: 390 },
            },
          },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            boxShadow: 1,
            display: 'flex',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            zIndex: 1,
          }}
        >
          {modalMode === 'copy' ? (
            <Typography sx={{ color: 'text.primary', pt: '2px' }} variant="h6">
              Copy Reconsignment
            </Typography>
          ) : modalMode === 'view' ? (
            <Typography sx={{ color: 'text.primary', pt: '2px' }} variant="h6">
              View Reconsignment
            </Typography>
          ) : (
            <Typography sx={{ color: 'text.primary', pt: '2px' }} variant="h6">
              Edit Reconsignment
            </Typography>
          )}
          <IconButton
            aria-label="closeIcon"
            onClick={() => handleClose()}
            sx={{
              alignSelf: 'flex-end',
              padding: 0,
            }}
          >
            <CloseRoundedIcon sx={{ color: 'primary.light' }} />
          </IconButton>
        </Box>
        <Box
          sx={{
            '&::-webkit-scrollbar': {
              display: 'none',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            },
            height: 'calc(100% - 129px)',
            overflowY: 'auto',
          }}
        >
          <Stack direction="row" justifyContent="space-between" sx={{ p: 3, pb: 2 }}>
            <Stack direction="column" spacing={2}>
              <Stack className="css-heap-redact" spacing={0.5}>
                <Typography className="css-heap-redact" variant="caption">
                  Reconsignment ID
                </Typography>
                <Typography className="css-heap-redact" variant="body1">
                  {R.prop('reconsignmentId', reconsignment)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption">Affiliate</Typography>
                <Typography variant="body1">
                  {R.prop('inventoryOwnerIdName', reconsignment)}{' '}
                  {R.prop('inventoryOwnerShortName', reconsignment)}
                </Typography>
              </Stack>
              {modalMode !== 'copy' && (
                <Stack spacing={0.5}>
                  <Typography variant="caption">Status</Typography>
                  {renderStatus(R.path(['reconsignment', 'status'], currentData))}
                </Stack>
              )}
              <Stack spacing={0.5}>
                <Typography variant="caption">Origin(s)</Typography>
                {R.gte(R.length(R.prop('origins', reconsignment)), 1) ? (
                  R.prop('origins', reconsignment).map(({ originId, originName }, idx, arr) => (
                    <Typography key={originId} variant="body1">
                      {originName}
                      {idx < arr.length - 1 ? ',' : ''}
                    </Typography>
                  ))
                ) : (
                  <Typography variant="body1">Any</Typography>
                )}
              </Stack>
            </Stack>
            <Stack direction="column" spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="caption">Consignment Status</Typography>
                <Typography variant="body1">
                  {R.prop('consignmentStatus', reconsignment) &&
                    capitalizeSingleWord(R.prop('consignmentStatus', reconsignment))}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption">Inventory Status</Typography>
                <Typography variant="body1">
                  {R.pipe(R.prop('inventoryStatus'), inventoryStatusCode =>
                    R.pipe(
                      R.find(R.propEq('code', inventoryStatusCode)),
                      R.propOr(inventoryStatusCode ?? '', 'label')
                    )(INVENTORY_STATUS_OPTIONS)
                  )(reconsignment)}
                </Typography>
              </Stack>
              <Stack spacing={0.5}>
                <Typography variant="caption">Product Status</Typography>
                <Typography variant="body1">
                  {R.prop('productStatus', reconsignment) &&
                    capitalizeSingleWord(R.prop('productStatus', reconsignment))}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Box sx={{ pb: 2, px: 3 }}>
            <Typography variant="caption">
              Note: Source locations are constrained by the origin(s), consignment status, inventory
              status, and product status configured above.
            </Typography>
          </Box>
          {R.and(
            R.not(R.isEmpty(R.path(['reconsignment', 'errorBookingResponse'], currentData))),
            R.equals('CANNOT BOOK', R.path(['reconsignment', 'status'], currentData)) ||
              R.equals('SCHEDULED', R.path(['reconsignment', 'status'], currentData))
          ) && (
            <Stack
              direction="row"
              spacing={2}
              sx={{
                alignItems: 'center',
                backgroundColor: 'success.light',
                borderRadius: 0.5,
                mx: 1,
                my: 2,
                px: 2,
                py: 1,
              }}
            >
              <InfoRoundedIcon sx={{ color: 'graphic.main' }} />
              <Typography sx={{ pt: '2px' }} variant="body2">
                {R.path(['reconsignment', 'errorBookingResponse'], currentData)}
              </Typography>
            </Stack>
          )}
          <Divider sx={{ margin: '0 auto', width: 'calc(100% - 48px)' }} />
          {R.prop('product', reconsignment) && (
            <Stack direction="column" spacing={2} sx={{ p: 3 }}>
              <DatePicker
                disabled={modalMode === 'view'}
                label="Transfer Date"
                minDate={startOfToday()}
                onAccept={() => setDatePickerOpen(false)}
                onChange={handleDateChange}
                onClose={() => setDatePickerOpen(false)}
                onOpen={() => {
                  // Only update if date is invalid or in the past
                  if (isDateInvalidOrPast(transferDateValue)) {
                    setReconsignment(
                      R.assoc('transferDate', format(startOfToday(), 'yyyy-MM-dd'), reconsignment)
                    );
                    validateTransferDate(startOfToday());
                  }
                  setDatePickerOpen(true);
                }}
                open={datePickerOpen}
                openTo="day"
                slotProps={{
                  textField: {
                    error: isError && modalMode !== 'view',
                    fullWidth: true,
                    helperText: modalMode !== 'view' ? helperTextCondition : '',
                    sx: { flex: 1 },
                  },
                }}
                value={transferDateValue}
              />
              <Autocomplete
                className="css-heap-redact"
                disableClearable
                disabled={modalMode === 'view'}
                getOptionLabel={o => (R.isEmpty(reconsignment) ? '' : R.prop('productName', o))}
                isOptionEqualToValue={R.eqProps('productId')}
                loading={isLoadingProducts}
                onChange={(_, value) => {
                  const isSelectedGasoline = value?.isGasoline === true;
                  setValidation(prev => R.assoc('toLocation', false, prev));
                  setReconsignment(prev =>
                    R.pipe(
                      R.assoc('product', value),
                      R.assoc('rvpValue', isSelectedGasoline ? 'Any' : ''),
                      R.assoc('fromLocation', null),
                      R.assoc('toLocation', null),
                      R.assoc('volume', '')
                    )(prev)
                  );
                }}
                options={products}
                renderInput={renderInput({
                  label: 'Product',
                  length: modalMode === 'view' ? 1 : products.length,
                  loading: isLoadingProducts,
                })}
                value={R.defaultTo({}, R.prop('product', reconsignment))}
              />
              <FormControl
                disabled={R.not(isGasoline) || modalMode === 'view'}
                error={rvpValidation}
                sx={{ flexBasis: 0, flexGrow: 1 }}
              >
                <InputLabel id="rvp">RVP</InputLabel>
                <Select
                  label="RVP"
                  labelId="rvp"
                  onChange={e =>
                    setReconsignment(
                      R.evolve({
                        fromLocation: R.always(null),
                        rvpValue: R.always(R.path(['target', 'value'], e)),
                      })
                    )
                  }
                  value={
                    modalMode === 'view'
                      ? reconsignment.rvpValue
                      : isGasoline
                      ? reconsignment.rvpValue || 'Any'
                      : ''
                  }
                >
                  {/* Ensures the current rvpValue is included in the view mode */}
                  {R.prop('rvpValue', reconsignment) &&
                    modalMode === 'view' &&
                    !rvps?.includes(R.prop('rvpValue', reconsignment)) && (
                      <MenuItem value={R.prop('rvpValue', reconsignment)}>
                        {R.prop('rvpValue', reconsignment)}
                      </MenuItem>
                    )}
                  {isGasoline ? (
                    rvps?.map(rvp => (
                      <MenuItem key={rvp} value={rvp}>
                        {rvp}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled value="">
                      No options
                    </MenuItem>
                  )}
                </Select>
                {rvpValidation && <FormHelperText>Required</FormHelperText>}
              </FormControl>
              <TextField
                className="css-heap-redact"
                disabled={modalMode === 'view'}
                error={R.not(R.isEmpty(R.prop('volume', validation)))}
                helperText={R.prop('volume', validation)}
                id="volume"
                label="Volume"
                onChange={handleVolumeChange}
                slotProps={{
                  input: {
                    endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
                  },
                }}
                value={formatNumberWithCommas(R.defaultTo('', R.prop('volume', reconsignment)))}
              />
              <Autocomplete
                disableClearable
                disabled={modalMode === 'view'}
                getOptionLabel={option => R.defaultTo('', R.prop('locationName', option))}
                isOptionEqualToValue={R.eqProps('locationId')}
                loading={isLoadingLocations}
                onChange={(_, value) => {
                  setValidation(prev => R.assoc('toLocation', false, prev));
                  setReconsignment(
                    R.evolve(
                      {
                        fromLocation: R.always(value),
                        toLocation: R.always(null),
                      },
                      reconsignment
                    )
                  );
                }}
                options={locations}
                renderInput={renderInput({
                  label: 'From Location',
                  length: modalMode === 'view' ? 1 : R.length(locations),
                  loading: isLoadingLocations,
                })}
                value={R.prop('fromLocation', reconsignment)}
              />
              <Autocomplete
                disableClearable
                disabled={modalMode === 'view' || R.isNil(R.prop('fromLocation', reconsignment))}
                getOptionLabel={option => R.defaultTo('', R.prop('locationName', option))}
                isOptionEqualToValue={R.eqProps('locationId')}
                loading={isLoadingDestinations}
                onChange={(_, value) => {
                  setValidation(prev => R.assoc('toLocation', false, prev));
                  setReconsignment(
                    R.evolve({
                      toLocation: R.always(value),
                    })
                  );
                }}
                options={R.defaultTo([], destinations)}
                renderInput={renderInput({
                  error: hasFromLocation && R.prop('toLocation', validation),
                  label: 'To Location',
                  length: modalMode === 'view' ? 1 : R.length(destinations),
                  loading: isLoadingDestinations,
                  showNoOptions:
                    modalMode === 'view' || R.not(R.isNil(R.prop('fromLocation', reconsignment))),
                })}
                value={R.prop('toLocation', reconsignment) || null}
              />
            </Stack>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              backgroundColor: 'surface.white',
              bottom: 0,
              boxShadow: 0,
              justifyContent: 'space-between',
              position: 'fixed',
              px: 3,
              py: 2,
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              width: { xs: 1, lg: 390 },
              zIndex: 1,
            }}
          >
            {modalMode === 'view' ? (
              <Button
                className="css-heap-redact"
                onClick={() => handleClose()}
                sx={{
                  mr: 2,
                  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                  width: '100%',
                }}
                variant="outlined"
              >
                Close
              </Button>
            ) : (
              <>
                <Stack direction="row" spacing={1}>
                  <Button
                    className="css-heap-redact"
                    onClick={() => handleClose()}
                    sx={{
                      mr: 2,
                      // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                      width: { xs: 1, lg: 159 },
                    }}
                    variant="outlined"
                  >
                    Close
                  </Button>
                  {modalMode === 'edit' ? (
                    <Button
                      className="css-heap-redact"
                      disabled={disableSave}
                      onClick={() => handleSave()}
                      sx={{
                        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                        width: { xs: 1, lg: 159 },
                      }}
                    >
                      {isUpdating ? <CircularProgress /> : 'Save'}
                    </Button>
                  ) : (
                    <Button
                      disabled={disableSave}
                      onClick={() => handleSubmit()}
                      sx={{
                        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                        width: { xs: 1, lg: 159 },
                      }}
                    >
                      {isUpdating ? <CircularProgress /> : 'Submit'}
                    </Button>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      </Drawer>
      <MDialog
        header="Unsaved Changes"
        leftButtonAction={() => {
          setOpenDialog(false);
          setSelectedReconsignment(null);
        }}
        leftButtonText="Continue"
        open={openDialog}
        rightButtonAction={() => setOpenDialog(false)}
        rightButtonText="Keep Editing"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          Are you sure you want to leave this page? You have unsaved reconsignment changes.
        </Typography>
      </MDialog>
    </>
  );
};

ManualDrawer.propTypes = {
  modalMode: string.isRequired,
  onReconsignmentUpdate: func.isRequired,
  reconsignmentInventoryOwner: PropTypes.shape({
    customerIdName: PropTypes.string,
    dataCompanyId: PropTypes.string,
  }).isRequired,
  selectedReconsignment: number,
  setSelectedReconsignment: func.isRequired,
};

ManualDrawer.defaultProps = {
  selectedReconsignment: null,
};

export default ManualDrawer;
